// Admin page for managing POS workstations at /admin/workstations.
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost, apiPatch, apiDelete } from "@/lib/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, THead, TBody, Tr, Th, Td } from "@/components/ui/table";
import { Plus, Pencil, Trash2 } from "lucide-react";

interface Workstation {
  id: string;
  name: string;
  printerIp: string;
  printerPort: number;
  isActive: boolean;
  warehouseId: string;
  warehouseName: string;
  createdAt: string;
}

interface Warehouse {
  id: string;
  name: string;
  isActive: boolean;
}

const EMPTY = { name: "", printerIp: "", printerPort: 9100, isActive: true, warehouseId: "" };

export function WorkstationsPage() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Workstation | null>(null);
  const [form, setForm] = useState(EMPTY);
  const [showForm, setShowForm] = useState(false);

  const { data: workstations = [] } = useQuery({
    queryKey: ["workstations"],
    queryFn: () => apiGet<Workstation[]>("/v1/workstations"),
  });

  const { data: warehouses = [] } = useQuery({
    queryKey: ["warehouses"],
    queryFn: () => apiGet<Warehouse[]>("/v1/warehouses"),
  });

  const activeWarehouses = warehouses.filter((w) => w.isActive);

  const saveMutation = useMutation({
    mutationFn: () => {
      if (editing) {
        return apiPatch<Workstation>(`/v1/workstations/${editing.id}`, form);
      }
      return apiPost<Workstation>("/v1/workstations", form);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["workstations"] });
      setShowForm(false);
      setEditing(null);
      setForm(EMPTY);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiDelete(`/v1/workstations/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["workstations"] }),
  });

  const openEdit = (w: Workstation) => {
    setEditing(w);
    setForm({
      name: w.name,
      printerIp: w.printerIp,
      printerPort: w.printerPort,
      isActive: w.isActive,
      warehouseId: w.warehouseId,
    });
    setShowForm(true);
  };

  const openNew = () => {
    setEditing(null);
    setForm({ ...EMPTY, warehouseId: activeWarehouses[0]?.id ?? "" });
    setShowForm(true);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Máy POS</h1>
        <Button onClick={openNew} size="sm">
          <Plus size={16} className="mr-1" /> Thêm máy
        </Button>
      </div>

      <Table>
        <THead>
          <Tr>
            <Th>Tên máy</Th>
            <Th>Kho</Th>
            <Th>IP máy in</Th>
            <Th>Cổng</Th>
            <Th>Trạng thái</Th>
            <Th className="w-24" />
          </Tr>
        </THead>
        <TBody>
          {workstations.map((w) => (
            <Tr key={w.id}>
              <Td className="font-medium">{w.name}</Td>
              <Td>{w.warehouseName || "—"}</Td>
              <Td className="font-mono text-sm">{w.printerIp || "—"}</Td>
              <Td>{w.printerPort}</Td>
              <Td>
                <span className={`text-xs font-medium ${w.isActive ? "text-green-600" : "text-muted-foreground"}`}>
                  {w.isActive ? "Hoạt động" : "Tắt"}
                </span>
              </Td>
              <Td>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" onClick={() => openEdit(w)}>
                    <Pencil size={14} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-destructive"
                    onClick={() => deleteMutation.mutate(w.id)}
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              </Td>
            </Tr>
          ))}
          {workstations.length === 0 && (
            <Tr><Td colSpan={6} className="text-center text-muted-foreground">Chưa có máy POS</Td></Tr>
          )}
        </TBody>
      </Table>

      {/* Form dialog */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-sm rounded-xl border bg-card p-6 shadow-xl space-y-4">
            <h2 className="font-semibold">{editing ? "Sửa máy POS" : "Thêm máy POS"}</h2>
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-sm font-medium">Tên máy</label>
                <Input value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">Kho</label>
                <select
                  className="h-9 w-full rounded-md border bg-background px-3 text-sm"
                  value={form.warehouseId}
                  onChange={(e) => setForm((p) => ({ ...p, warehouseId: e.target.value }))}
                >
                  <option value="">— Chọn kho —</option>
                  {activeWarehouses.map((w) => (
                    <option key={w.id} value={w.id}>{w.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">IP máy in</label>
                <Input
                  value={form.printerIp}
                  onChange={(e) => setForm((p) => ({ ...p, printerIp: e.target.value }))}
                  placeholder="192.168.1.100"
                  className="font-mono"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">Cổng</label>
                <Input
                  type="number"
                  value={form.printerPort}
                  onChange={(e) => setForm((p) => ({ ...p, printerPort: parseInt(e.target.value) || 9100 }))}
                />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(e) => setForm((p) => ({ ...p, isActive: e.target.checked }))}
                />
                Hoạt động
              </label>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setShowForm(false)} className="flex-1">Hủy</Button>
              <Button
                onClick={() => saveMutation.mutate()}
                disabled={!form.name.trim() || !form.warehouseId || saveMutation.isPending}
                className="flex-1"
              >
                {saveMutation.isPending ? "Đang lưu..." : "Lưu"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
